import type { CountryHitTester } from '../interactions/point-in-country';
import type { BakedFillData } from '../fills/polygon-fills';

/**
 * Module-scoped cache of the heavy outputs produced on first
 * `MapEngine.loadBorders()` call. Reused across subsequent engine
 * instances so cross-mode navigation between country/quiz modes
 * doesn't pay the ~500–800ms triangulation pass on every switch.
 *
 * What's safe to cache here:
 *   - Pure typed arrays (`borderPositions`, `BakedFillData.positions`,
 *     `BakedFillData.indices`) — no GPU/WebGL coupling, can seed a
 *     fresh BufferGeometry on each engine.
 *   - Plain JS objects (`hitTester`) — `CountryHitTester.find()` is
 *     read-only, so the same instance is safe to wire into multiple
 *     dispatchers across engines.
 *
 * What's NOT cached here:
 *   - Three.js meshes / materials / geometries — these own WebGL
 *     resources tied to the renderer that created them; they MUST be
 *     created fresh per engine.
 *   - Per-country fill state (selected/correct/inactive/...) — that's
 *     per-mode and reapplied freshly on top of the rebuilt mesh.
 *   - Raw datasets (countries.json, country-polygons.json) — already
 *     cached at the `data/loader.ts` per-URL promise level, so a second
 *     `loadCountryPolygons()` call resolves instantly.
 */
interface BordersCache {
  borderPositions: Float32Array | null;
  fillBaked: BakedFillData | null;
  hitTester: CountryHitTester | null;
}

export const bordersCache: BordersCache = {
  borderPositions: null,
  fillBaked: null,
  hitTester: null,
};
